/* eslint-disable @typescript-eslint/no-use-before-define */

import { useCallback } from 'react';
import { useQuery } from '@apollo/client';

import { KybDocumentType } from '__generated__/GQL';
import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import SuspenseQuery from 'components/SuspenseQuery';

import OBLoaderPage from '../../components/OBLoaderPage/OBLoaderPage';
import { CapitalDepositProofOfFundsDocument } from './graphql/queries/CapitalDepositProofOfFunds.gql';
import machineConfig, { model, State } from './machine';
import { KybDocumentFile, ProofOfFundsDocumentType } from './machine/model';
import ProofOfFundsCheck from './pages/ProofOfFundsCheck';
import ProofOfFundsHub from './pages/ProofOfFundsHub';
import ProofOfFundsReview from './pages/ProofOfFundsReview';
import ProofOfFundsUpload from './pages/ProofOfFundsUpload';

type ProofOfFundsUploadProps = {
  companyProfileId: string;
};

const CapitalDepositProofOfFunds = asBentoModule<ProofOfFundsUploadProps>(
  ({ companyProfileId, onDone }) => {
    const proofOfFundsQuery = useQuery(CapitalDepositProofOfFundsDocument, {
      variables: { companyProfileId },
    });

    return (
      <SuspenseQuery
        loader={<OBLoaderPage onPrev={() => null} />}
        query={proofOfFundsQuery}
      >
        {(proofOfFundsModuleData) => (
          <ProofOfFundsComponent
            moduleData={{
              proofOfFundsData: proofOfFundsModuleData,
            }}
            onDone={onDone}
          />
        )}
      </SuspenseQuery>
    );
  },
);

const ProofOfFundsComponent = withStateMachine<typeof model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;

    const onPrev = useCallback(() => {
      send(model.events.PREV());
    }, [send]);

    const { company } = moduleData.proofOfFundsData.viewer;

    const document = company.kybDocuments.find(
      ({ type }) => type === KybDocumentType.ProofOfFunds,
    );

    const stringifiedState = stringifyState<State>(state);

    switch (stringifiedState) {
      case State.CHOOSE_DOCUMENT_TYPE:
        return (
          <ProofOfFundsHub
            onPrev={onPrev}
            onSelectedOption={(option: ProofOfFundsDocumentType) => {
              send(model.events.SELECT_DOCUMENT_TYPE(option));
            }}
          />
        );

      case State.REVIEW:
      case State.DELETE:
        return (
          <ProofOfFundsReview
            onContinue={() => send(model.events.NEXT())}
            onPrev={onPrev}
            review={
              document
                ? {
                    document,
                    isDeleting: state.matches(State.DELETE),
                    onDelete: (kybDocumentId) =>
                      send(model.events.DELETE_DOCUMENT(kybDocumentId)),
                  }
                : undefined
            }
          />
        );

      case State.UPLOAD:
        if (!state.context.selectedOption) {
          throw new Error('selectedOption cannot be null');
        }
        return (
          <ProofOfFundsUpload
            companyProfileId={company.companyProfileId}
            onPrev={onPrev}
            onUploadComplete={(documentFile: KybDocumentFile) =>
              send(model.events.SUBMIT_DOCUMENT_FILE(documentFile))
            }
            selectedOption={state.context.selectedOption}
          />
        );

      case State.CHECK:
        if (!state.context.selectedOption) {
          throw new Error('selectedOption cannot be null');
        }

        if (!state.context.documentFile?.url) {
          throw new Error('documentFile cannot be null');
        }

        return (
          <ProofOfFundsCheck
            documentUrl={state.context.documentFile?.url}
            onPrev={onPrev}
            onValidate={() => send(model.events.NEXT())}
            selectedOption={state.context.selectedOption}
          />
        );

      case State.INIT:
      case State.SEND:
      case State.DONE:
      case State.ABORT:
        return <OBLoaderPage onPrev={() => null} />;

      default:
        return assertNever(stringifiedState);
    }
  },
  {
    devTools: true,
    machineConfig,
  },
);

export default CapitalDepositProofOfFunds;
